import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../services/api';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { getErrorMessage } from '../utils/errorMessage';
import { clubLogoSrc, handleClubLogoError } from '../utils/clubLogo';
import BrandLockup from '../components/ui/BrandLockup';

/**
 * Elfelejtett jelszó: a megadott címre visszaállító linket küldünk. A válasz
 * szándékosan ugyanaz, akár létezik a fiók, akár nem.
 */
const ForgotPassword: React.FC = () => {
  const location = useLocation();
  const { settings } = useClubSettings();
  usePageTitle('Elfelejtett jelszó');

  // A belépő oldalról átvisszük a már beírt címet, hogy ne kelljen újra gépelni
  const initialEmail = (location.state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentMessage, setSentMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const result = await authAPI.forgotPassword(email.trim());
      setSentMessage(result.message);
    } catch (err) {
      setError(getErrorMessage(err, 'A kérést nem sikerült elküldeni. Próbáld újra később.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        {settings?.clubName && (
          <div className="mb-4 flex justify-center">
            <img
              src={clubLogoSrc(settings.crestUrl)}
              alt=""
              className="h-20 w-20 object-contain"
              onError={handleClubLogoError}
            />
          </div>
        )}

        {settings?.clubName ? (
          <h1 className="text-center text-3xl font-extrabold text-gray-900">{settings.clubName}</h1>
        ) : (
          <h1 className="flex justify-center">
            <BrandLockup size="lg" />
          </h1>
        )}

        {sentMessage ? (
          <div className="mt-8 rounded-lg bg-white p-6 text-left shadow-sm">
            <div className="flex items-start gap-3">
              <EnvelopeIcon className="h-6 w-6 flex-shrink-0 text-primary-600" />
              <div>
                <p className="font-medium text-gray-900">Nézd meg a postafiókodat</p>
                <p className="mt-1 text-sm text-gray-500">{sentMessage}</p>
                <p className="mt-3 text-sm text-gray-500">
                  A link 1 óráig érvényes. Ha nem érkezik meg, nézd meg a spam mappát is.
                </p>
              </div>
            </div>
            <Link
              to="/login"
              className="mt-4 block w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Vissza a bejelentkezéshez
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-lg bg-white p-6 text-left shadow-sm">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Elfelejtett jelszó</h2>
              <p className="mt-1 text-sm text-gray-500">
                Add meg a fiókodhoz tartozó email címet, és küldünk egy linket, amellyel új jelszót állíthatsz be.
              </p>
            </div>

            <div>
              <label htmlFor="forgot-email" className="mb-1 block text-sm font-medium text-gray-700">
                Email cím
              </label>
              <input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>

            {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Küldés...' : 'Visszaállító link küldése'}
            </button>

            <p className="text-center text-sm text-gray-600">
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Vissza a bejelentkezéshez
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
